import React, {FunctionComponent, useEffect, useRef, useState} from "react"
import {useNavigate} from "react-router-dom"
import Academy from "@murphy-frontend/web-core/assets/icons/Academy.svg"
import Standard from "@murphy-frontend/web-core/assets/icons/Standard.svg"
import Exercises from "@murphy-frontend/web-core/assets/icons/Exercises.svg"
import Incidents from "@murphy-frontend/web-core/assets/icons/Incidents.svg"
import Elearn from "@murphy-frontend/web-core/assets/icons/Elearn.svg"
import {BsExclamationCircle} from "react-icons/bs"
import {RiBroadcastFill, RiBookOpenLine} from "react-icons/ri"
import {GiBullseye, GiPolarStar} from "react-icons/gi"
import {BiSolidPyramid} from "react-icons/bi"

import PortalModule from "./PortalModule"
import {useModal} from "@murphy-frontend/web-core/contexts/ModalContext"
import {ModalConfiguration} from "@murphy-frontend/web-core/components/Modal"
import {useUserPreferences} from "../../common/contexts/UserPreferencesContext"
import {enabledProducts} from "../../common/variables"
import {useUser} from "../../common/contexts/UserContext"
import {Step, Steps} from "intro.js-react"
import {OnboardingFlowIds, useOnboarding} from "../../common/contexts/OnboardingContext"
import {IconContext} from "react-icons"
import {useInjection} from "@murphy-frontend/common/contexts/InversifyContext"
import ApiServiceType, {IApiService} from "@murphy-frontend/common/interfaces/IApiService"
import {openInNewTab} from "@murphy-frontend/common/utils"
import {OnlineEducationApi} from "../OnlineEducation/api/OnlineEducationApi"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import InfoIcon from "@murphy-frontend/web-core/components/InfoIcon"
import Button from "@murphy-frontend/web-core/components/Button"
import { IoIosCheckmark } from "react-icons/io"

const Portal: FunctionComponent = () => {
	const {openModal, closeModal} = useModal()
	const {translations} = useUserPreferences()

	const navigate = useNavigate()
	const {user} = useUser()
	const {setOnboardingIdIsComplete, incompletedOnboardingFlows, resetOnboarding, setCurrentStep, onboardingsCurrentStep} = useOnboarding()

	const apiService = useInjection<IApiService>(ApiServiceType.IApiService)
	// produce state variables for the 5 consts above
	const [trainingEnabled, setTrainingEnabled] = useState<boolean>(null)
	const [mbsEnabled, setMbsEnabled] = useState<boolean>(null)
	const [crisisEnabled, setCrisisEnabled] = useState<boolean>(null)
	const [guidanceEnabled, setGuidanceEnabled] = useState<boolean>(null)
	const [excersisesEnabled, setExcersisesEnabled] = useState<boolean>(null)
	const [pyramidEnabled, setPyramidEnabled] = useState<boolean>(null)
	const [eventFlowEnabled, setEventFlowEnabled] = useState<boolean>(null)
	const [elearningSweEnabled, setElearningsweEnabled] = useState<boolean>(null)
	const [elearningFtgEnabled, setElearningftgEnabled] = useState<boolean>(null)
	const [onboardingTerminated, setOnboardingTerminated] = useState<boolean>(null)
    const stepsRef = useRef(null);
	// make a useEffect that sets these state variables based on user
	useEffect(() => {
		setTrainingEnabled(user?.traningEnabled && enabledProducts.training === "true")
		setMbsEnabled(user?.mbsEnabled && enabledProducts.mbs === "true")
		setCrisisEnabled(user?.whiteEnabled && enabledProducts.white === "true")
		setGuidanceEnabled(user?.guidanceEnabled)
		setExcersisesEnabled(user?.excersisesEnabled && enabledProducts.exercise === "true")
		setPyramidEnabled(user?.myMurphyEnabled)
		setEventFlowEnabled(user?.eventFlowsEnabled)
		setElearningsweEnabled(user?.elearningSweEnabled)
		setElearningftgEnabled(user?.elearningFtgEnabled)
	}, [user])


	const tutorialStep = (stepNo: number, totalSteps: number, title: string, body1: string, body2: string, img, subTitle?: string) => {
		return <div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', flexDirection: 'row' }}>
				<label style={{ color: '#00A098', fontWeight: 'bold', marginRight: 5 }}>{stepNo}</label>
				<label>of</label>
				<label style={{ marginLeft: 5 }}>{totalSteps}</label>
			</div>
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 10, marginBottom: 20 }}>
				<img src={img} style={{ width: '16rem' }} />
			</div>
			<div style={{ flexDirection: 'column', display: 'flex' }}>
				<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold' }}>{title}</label>
				{subTitle && <label style={{ marginBottom: 10, fontSize: 14, fontWeight: 'bold' }}>{subTitle}</label>}
				<label>{body1}</label>
				<label style={{ marginTop: 10 }}>{body2}</label>
			</div>
		</div>
	}

	// Create initialSteps based on what is enabled
	const createInitialSteps = (): Step[] => {
		const steps = Array<Step>()
		if (trainingEnabled) {
			steps.push({
				element: ".background-item1",
				intro: tutorialStep(1, 5, "Step 1: Academy", "This is the Academy. Start learning about crisis response, team preparedness, and Murphy’s core platform tools.", "Build your knowledge and become a more confident crisis responder.", require('../../assets/img/academy.png'))
			})
		}
		// if (excersisesEnabled) {
		// 	steps.push({
		// 		element: ".background-item2",
		// 		intro: `${translations.excercisesmodulename}: Time to move from theory to practice! Here you can find the exercises you have in your organization or book an exercise to level up your crisis management capabilities.`
		// 	})
		// }
		// if (guidanceEnabled) {
		// 	steps.push({
		// 		element: ".background-item6",
		// 		intro: `${translations.guidance}: a place to take part of pre-recorded material as a guidance on how to handle a crisis situation.`
		// 	})
		// }
		if (pyramidEnabled) {
			steps.push({
				element: ".background-item5",
				intro: tutorialStep(2, 5, "Step 2: The Crisis Framework", "Here you’ll find Level 1 assessments across key areas like People, Communication, Tools, and Organisation.", "It’s your starting point to evaluate how prepared your team is — and what to work on next.", require('../../assets/img/crisis.png'), "Meet the TCF pyramid.")
			})
		}
		if (mbsEnabled) {
			steps.push({
				element: ".background-item3",
				intro: tutorialStep(3, 5, "Step 3: Alerts", "The Alerts tab helps you quickly notify your team through email or in-app messages.", "Freemium includes two-way communication using one default template — perfect for updates, drills, or basic response coordination.", require('../../assets/img/alerts.png'))
			})
		}
		if (crisisEnabled) {
			steps.push({
				element: ".background-item4",
				intro: tutorialStep(4, 5, "Step 4: Operations", "Track actions in real time with your Operations dashboard.", "You’ll find one pre-built template ready to go. Use it to visualise your response activities and keep everyone aligned", require('../../assets/img/operations.png'))
			})
		}
		if (eventFlowEnabled) {
			steps.push({
				element: ".background-item7",
				intro: tutorialStep(5, 5, "Step 5: Incidents", "Murphy’s Incident tab shows how you can structure complex events and crisis scenarios.", "In Freemium, this feature is view-only — giving you a preview of what’s possible in Premium", require('../../assets/img/incidents.png'))
			})
		}
		// if (elearningSweEnabled) {
		// 	steps.push({
		// 		element: ".background-item6",
		// 		intro: `${translations.elearningswemodulename}: E-learning för anställda.`
		// 	})
		// }
		// if (elearningFtgEnabled) {
		// 	steps.push({
		// 		element: ".background-item8",
		// 		intro: `${translations.elearningftgmodulename}: E-learning for FTG.`
		// 	})
		// }

		// steps.push({
		// 	element: ".card-icon",
		// 	intro: `Profile: This is where you can find your own info such as the courses you have attended and reach other customers in your organizaton.`
		// })

		// steps.push({
		// 	element: ".menu-icon2",
		// 	intro: `Settings: Change system language and reach our knowledge base. As an Admin this is where you find your tools!`
		// })

		// steps.push({
		// 	element: ".menu-icon",
		// 	intro: `Menu: Reach all the modules including the settings for the Alert module.`
		// })

		// set tooltipClass to 'tooltip-class' on all steps
		steps.forEach(step => {
			step.tooltipClass = "tooltip-class"
		})

		return steps
	}

	const [steps, setSteps] = useState<Step[]>([])
	const [initialStep, setInitialStep] = useState<number>(0)
	const [stepsEnabled, setStepsEnabled] = useState<boolean>(false)

	const onExit = (stepIndex: number) => {
		console.log('Onboarding is exited from step ' + stepIndex);

		// sometimes introjs calls onExit by itself, but with -1 as stepIndex. When the user does it, it will be with a positive number.
		if (stepsRef?.current != null && stepIndex > -1) {
			if (stepsRef?.current.introJs._lastShowElementTimer) {
				onComplete();
			}
		}
	};

	const onComplete = () => {
				setOnboardingTerminated(true)
				setOnboardingIdIsComplete(OnboardingFlowIds.Portal)
				setCurrentStep(OnboardingFlowIds.Portal, 0)
				setStepsEnabled(false)
	}

	const MissingProductMessage = () => <div>{translations.unauthorized}</div>

	const onMissingProduct = () => {
		const modalConf: ModalConfiguration = {
			title: translations.unauthorized,
			body: <MissingProductMessage />,
			buttonText: "Ok",
			type: "warning"
		}

		openModal(modalConf)
	}

	const onClickEducation = () => {
		if (trainingEnabled === true) {
			navigate("/courses")
		} else {
			onMissingProduct()
		}
	}

	const EducationModule = (
		<PortalModule
			onClick={onClickEducation}
			buttonIcon={
				<div className='module-custom-icon'>
					<Academy />
				</div>
			}
			title={translations.coursesmodulename}
			footer='Live training from crisis experts'
			customClasses='background-item1'
		/>
	)

	const onClickTraining = () => {
		navigate("/excersises")
	}

	const TrainingModule = (
		<PortalModule
			onClick={onClickTraining}
			buttonIcon={
				<div className='module-custom-icon'>
					<Exercises />
				</div>
			}
			title={translations.excercisesmodulename}
			footer='Train to be prepared'
			customClasses='background-item2'
		/>
	)

	const onClickGuidance = () => {
		if (guidanceEnabled === true) {
			navigate("/guidance")
		} else {
			onMissingProduct()
		}
	}
	const GuidanceModule = (
		<PortalModule
			onClick={onClickGuidance}
			buttonIcon={
				<IconContext.Provider value={{className: ""}}>
					<GiPolarStar size={40} />
				</IconContext.Provider>
			}
			title={translations.guidance}
			footer="I'M TOLD THERE'S NO GOING BACK. SO I'M CHOOSING FORWARD"
			customClasses='background-item6'
		/>
	)

	const onClickMBS = () => {
		if (mbsEnabled === true) {
			navigate("/pbscreatenew")
		} else {
			onMissingProduct()
		}
	}

	const MBSModule = (
		<PortalModule
			onClick={onClickMBS}
			title={translations.mbsmodulename}
			buttonIcon={
				<IconContext.Provider value={{className: ""}}>
					<RiBroadcastFill size={40} />
				</IconContext.Provider>
			}
			footer='Let them know. Now.'
			customClasses='background-item3'
		/>
	)

	const onClickCrisis = () => {
		if (crisisEnabled === true) {
			navigate("/crisis")
		} else {
			onMissingProduct()
		}
	}

	const CrisisModule = (
		<PortalModule
			onClick={onClickCrisis}
			buttonIcon={
				<IconContext.Provider value={{className: ""}}>
					<BsExclamationCircle size={40} />
				</IconContext.Provider>
			}
			title={translations.whitemodulename}
			footer='Rule complexity with simplecity.'
			customClasses='background-item4'
		/>
	)

	const onClickPyramid = () => {
		if (pyramidEnabled === true) {
			navigate("/tmscategory")
		} else {
			onMissingProduct()
		}
	}

	const PyramidModule = (
		<PortalModule
			onClick={onClickPyramid}
			buttonIcon={
				<div className='module-custom-icon'>
					<Standard />
				</div>
			}
			title={translations.pyramidmodulename}
			footer='Evaluate your preparedness'
			customClasses='background-item5'
		/>
	)

	const onClickEventFlows = () => {
		if (eventFlowEnabled === true) {
			navigate("/startedeventflows")
		} else {
			onMissingProduct()
		}
	}

	const EventFlowModule = (
		<PortalModule
			onClick={onClickEventFlows}
			buttonIcon={
				<div className='module-custom-icon'>
					<Incidents />
				</div>
			}
			title={translations.eventflowsmodulename}
			footer='Activate your protocols'
			customClasses='background-item7'
		/>
	)

	const onClickElearningSwe = async () => {
		if (elearningSweEnabled === true) {
			const onlineEducationApi = new OnlineEducationApi(apiService)
			try {
				const tokenUrl = await onlineEducationApi.getTokenUrl(
					"https://edu.murphysolution.com/courses/take/grunder-inom-krisledning-och-krisberedskap/lessons/58056565-introduktion-till-krishantering"
				)
				openInNewTab(tokenUrl)
			} catch (error) {
				console.error("Error fetching token URL:", error)
				// Handle error, e.g., show an error message to the user
			}
		} else {
			onMissingProduct()
		}
	}

	const ElearningsweModule = (
		<PortalModule
			onClick={onClickElearningSwe}
			buttonIcon={
				<div className='module-custom-icon'>
					<Elearn />
				</div>
			}
			title={translations.elearningswemodulename}
			footer=''
			customClasses='background-item6'
		/>
	)

	const onClickElearningFtg = async () => {
		if (elearningFtgEnabled === true) {
			const onlineEducationApi = new OnlineEducationApi(apiService)
			try {
				const tokenUrl = await onlineEducationApi.getTokenUrl(
					"https://edu.murphysolution.com/courses/take/crisis-management-ftg/lessons/56340260-murphy-vetalum-welcome"
				)
				openInNewTab(tokenUrl)
			} catch (error) {
				console.error("Error fetching token URL:", error)
				// Handle error, e.g., show an error message to the user
			}
		} else {
			onMissingProduct()
		}
	}

	const ElearningFtgModule = (
		<PortalModule
			onClick={onClickElearningFtg}
			buttonIcon={
				<div className='module-custom-icon'>
					<Elearn />
				</div>
			}
			title={translations.elearningftgmodulename}
			footer='Prepare yourself with courses'
			customClasses='background-item6'
		/>
	)

	useEffect(() => {
		if (steps && steps.length > 0) {
			const shouldShowOnboarding = incompletedOnboardingFlows
				.map(p => p.OnboardingFlowId)
				.includes(OnboardingFlowIds.Portal)
			setCurrentStep(OnboardingFlowIds.Portal, 1)
			setStepsEnabled(shouldShowOnboarding)
		}
	}, [steps])

	const onClickStartOnboarding = () => {
		resetOnboarding(OnboardingFlowIds.Portal);
		const newSteps = createInitialSteps()
		setSteps(newSteps)
		const firstStep = onboardingsCurrentStep.get(OnboardingFlowIds.Portal);
		setInitialStep(firstStep);

	}

	const points = [
		"Level 1 access across all TCF pyramids",
		"4 beginner-level Academy courses in English",
		"Two-way Alerts via email and notifications",
		"One ready-to-use alert template",
		"Full access to the Files module"
	]
	const renderPoints = () => {
		return <div>
			{points.map(point => <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
				<IoIosCheckmark color="#00B2A9" size={30} />
				<label style={{ color: '#626262', marginLeft: 5 }}>{point}</label>
			</div>)}
		</div>
	}

	const murphyGuideModal = () => {
		const modalConf: ModalConfiguration = {
			okCallback: () => { console.log("onclick") },
			onCloseModal: () => { console.log("on close") },
			type: 'info',
			centerContent: false,
			body: <div style={{ display: 'flex', flexDirection: 'column' }}>

				<div style={{ width: '20rem', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 10, marginBottom: 20 }}>
					<img src={require('../../assets/img/murphyplanstart.png')} style={{ height: 100, display: 'block' }} />
				</div>
				<div style={{ flexDirection: 'column', display: 'flex', width: '20rem' }}>
					<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold' }}>Welcome to Murphy</label>
					<label>This quick guide will show you what’s available in your plan and how to get started.</label>
					<label style={{ marginTop: 10 }}>Explore the tools designed to help your team prepare, respond, and improve — one step at a time.</label>
				</div>

				<div style={{ width: '10rem', alignSelf: 'center', marginTop: 20, display: 'flex', justifyContent: 'center' }}>
					<Button buttonStyle='btn--primary' onClick={() => { closeModal(); onClickStartOnboarding() }}>
						Get Started
					</Button>
				</div>


			</div>,
			hideButton: true,
			hideCloseIcon: true,
			position: 'center'
		}
		openModal(modalConf);
	}
	const onClickStartTutorial = () => {
		const modalConf: ModalConfiguration = {
			okCallback: () => { console.log("onclick") },
			onCloseModal: () => { console.log("on close") },
			type: 'tutorial',
			centerContent: false,
			body: <div>
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<div style={{ height: '30rem', justifyContent: 'center', alignItems: 'center', overflow:'hidden' }}>
						<img src={require('../../assets/img/onboarding.png')} style={{height: '30rem'}}/>
					</div>
					<div style={{ width: '30rem', paddingLeft: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
						<label style={{ marginBottom: 5, fontSize: 16, fontWeight: 'bold' }}>Hi {user?.CustomerName}!</label>
						<label style={{ marginBottom: 10, fontSize: 18, fontWeight: 'bold' }}>Welcome to Murphy</label>
						{renderPoints()}
						<div style={{ width: '20rem', alignSelf: 'flex-end', marginTop: 30 }}>
							<Button buttonStyle='btn--primary' onClick={() => { closeModal(); murphyGuideModal() }}>
								Start Guide now
							</Button>
						</div>

					</div>
				</div>
			</div>,
			hideButton: true,
			position: 'center'
		}
		openModal(modalConf);
	}

	const stepsIcon = (
		<InfoIcon
			place={"right-end"}
			onHoverContents={<Button onClick={onClickStartTutorial}>Start tutorial</Button>}
			clickable={true}
		/>
	)

	return (
		<ComponentWrapper active={1}>
			<div style={{flexDirection: "column", display: "flex", paddingLeft: 20, paddingRight: 20}}>
				<div className='one header-with-info-button'>
					<label style={{ fontWeight: "bold", fontSize: 24, color: "black" }}>
						{translations.welcome || "Welcome"}
					</label>
					{/* {stepsIcon} */}
				</div>
				<Steps
					ref={stepsRef}
					enabled={stepsEnabled}
					steps={steps}
					initialStep={initialStep}
					onExit={onExit}
					onComplete={onComplete}
					options={{ hideNext: false, hidePrev: true, exitOnOverlayClick: false, showBullets: false }}
				/>

				<div className='vertical-aligner horizontal-aligner'>
					<div className='portal-grid'>
						{trainingEnabled && (
							<>
								<div className='vertical-aligner horizontal-aligner'>{EducationModule}</div>
							</>
						)}
						{pyramidEnabled && <div className='vertical-aligner horizontal-aligner'>{PyramidModule}</div>}
						{excersisesEnabled && (
							<div className='vertical-aligner horizontal-aligner'>{TrainingModule}</div>
						)}
						{guidanceEnabled && <div className='vertical-aligner horizontal-aligner'>{GuidanceModule}</div>}
						{mbsEnabled && <div className='vertical-aligner horizontal-aligner'>{MBSModule}</div>}
						{crisisEnabled && <div className='vertical-aligner horizontal-aligner'>{CrisisModule}</div>}
						{eventFlowEnabled && (
							<div className='vertical-aligner horizontal-aligner'>{EventFlowModule}</div>
						)}
						{elearningSweEnabled && (
							<div className='vertical-aligner horizontal-aligner'>{ElearningsweModule}</div>
						)}
						{elearningFtgEnabled && (
							<div className='vertical-aligner horizontal-aligner'>{ElearningFtgModule}</div>
						)}
					</div>
				</div>
			</div>
		</ComponentWrapper>
	)
}

export default Portal
